import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceApi } from '@app/services/invoice-api';
import {
  InvoiceInterface,
  CurrencyCode,
  PaymentMethodCode,
  PensionFundOptions,
} from '@app/interface/invoice';
import { HttpEventType } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgForm } from '@angular/forms';
import { OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyApi } from '@app/services/company-api';
import * as XLSX from 'xlsx';

interface ExcelPreview {
  sheetName: string;
  headers: string[];
  totalRows: number;
  sampleData: any[][];
  totalSheets: number;
}

interface UploadResult {
  message: string;
  imported_count: number;
  failed_count: number;
  failed_rows: string[];
  sample_imported?: any[]; // Adjust based on your actual API response
}

@Component({
  selector: 'izy-upload-company-list',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    BackHeaderComponent,
  ],
  templateUrl: './upload-company-list.component.html',
  styleUrl: './upload-company-list.component.scss',
})
export class UploadCompanyListComponent implements OnInit {
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Excel preview properties
  excelPreview: ExcelPreview | null = null;

  // Upload result properties
  uploadResult: UploadResult | null = null;

  uploadForm: FormGroup;
  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  uploadSuccess = false;
  xmlContent: string | null = null; // To store the XML content

  constructor(
    private fb: FormBuilder,
    private companyApi: CompanyApi,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.uploadForm = this.fb.group({
      organization_id: ['', [Validators.required, Validators.min(1)]],
    });
  }

  ngOnInit() {
    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId')
    );

    this.uploadForm.patchValue({
      organization_id: organizationId,
    });
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'));

    if (file && validExtensions.includes(fileExtension)) {
      this.selectedFile = file;
      this.uploadError = null;
      this.excelPreview = null; // Reset previous content

      console.log('Excel file selected:', this.selectedFile);

      // Read and preview Excel content
      this.readExcelFile(file);
    } else {
      this.uploadError = 'Please select a valid Excel file (.xlsx or .xls)';
      this.selectedFile = null;
      this.excelPreview = null;
    }
  }

  private readExcelFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);

        console.log('=== EXCEL FILE INFO ===');
        console.log('File name:', file.name);
        console.log('File size:', file.size, 'bytes');
        console.log('File type:', file.type);

        // Parse Excel file
        this.parseExcelContent(data, file);
      } catch (error) {
        console.error('Error reading Excel file:', error);
        this.uploadError = 'Error reading Excel file';
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      this.uploadError = 'Error reading file';
    };

    // Read as array buffer for Excel files
    reader.readAsArrayBuffer(file);
  }

  private parseExcelContent(data: Uint8Array, file: File): void {
    try {
      // For client-side parsing, you can use a library like xlsx
      // Note: You'll need to install xlsx: npm install xlsx
      const workbook = XLSX.read(data, { type: 'array' });

      console.log('=== EXCEL FILE CONTENT ===');
      console.log('Workbook Sheet Names:', workbook.SheetNames);

      // Get the first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convert to JSON for preview
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      console.log('Excel data (first 10 rows):', jsonData.slice(0, 10));
      console.log('Total rows:', jsonData.length);

      // Store preview data
      this.excelPreview = {
        sheetName: firstSheetName,
        headers: jsonData[0] as string[], // First row as headers
        totalRows: jsonData.length - 1, // Exclude header row
        sampleData: jsonData.slice(1, 6) as any[], // First 5 data rows for preview
        totalSheets: workbook.SheetNames.length,
      };

      console.log('Headers:', this.excelPreview.headers);
      console.log('Sample data rows:', this.excelPreview.sampleData);
      console.log('=== END EXCEL CONTENT ===');
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      this.uploadError =
        "Error parsing Excel file. Please ensure it's a valid Excel file.";
    }
  }

  // Optional: Method to display Excel preview in template
  getExcelPreview(): any {
    if (!this.excelPreview) return null;

    return {
      fileName: this.selectedFile?.name,
      sheetName: this.excelPreview.sheetName,
      totalSheets: this.excelPreview.totalSheets,
      totalRows: this.excelPreview.totalRows,
      headers: this.excelPreview.headers,
      sampleData: this.excelPreview.sampleData,
    };
  }

  // Get column mapping suggestions based on headers
  getColumnMappingSuggestions(): { [key: string]: string } {
    if (!this.excelPreview?.headers) return {};

    const suggestions: { [key: string]: string } = {};
    const headers = this.excelPreview.headers.map((h: string) =>
      h ? h.toString().toLowerCase().trim().replace(/\s+/g, '_') : ''
    );

    // Common mappings with proper type definition
    const commonMappings: { [key: string]: string } = {
      denominazione: 'company_name',
      codice_interno: 'internal_code',
      indirizzo: 'address',
      comune: 'city',
      cap: 'cap',
      provincia: 'province',
      paese: 'country',
      email: 'email_address',
      'indirizzo_e-mail': 'email_address',
      referente: 'referent',
      telefono: 'telephone',
      piva: 'iva_number',
      partita_iva: 'iva_number',
      codice_fiscale: 'c_f',
      cf: 'c_f',
      note: 'notes',
      pec: 'pec',
      tipo_cliente: 'type',
    };

    headers.forEach((header: string, index: number) => {
      // Use type assertion or check for existence
      if (header in commonMappings) {
        const excelHeader = this.excelPreview!.headers[index]; // Non-null assertion since we checked excelPreview exists
        suggestions[excelHeader] = commonMappings[header];
      }
    });

    return suggestions;
  }

  onSubmit(): void {
    if (this.uploadForm.valid && this.selectedFile) {
      this.uploadCompanies();
    }
  }

  private uploadCompanies(): void {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadError = null;
    this.uploadSuccess = false;

    const organizationId = Number(
      this.route.snapshot.paramMap.get('organizationId')
    );

    this.companyApi // Make sure you have this service injected
      .uploadCompanyExcel(organizationId, this.selectedFile!)
      .subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round(
              (100 * event.loaded) / event.total
            );
          } else if (event.type === HttpEventType.Response) {
            this.isUploading = false;
            this.uploadSuccess = true;
            this.uploadResult = event.body; // Store the response data
            this.uploadForm.reset();
            this.selectedFile = null;
            this.excelPreview = null;
            setTimeout(() => {
              this.uploadSuccess = false;
              this.uploadResult = null;
            }, 5000);
          }
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadError =
            error.error?.error ||
            error.error?.message ||
            'Upload failed. Please try again.';
          console.error('Upload error:', error);
        },
      });
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }
}
